import { EDITORIAL_REVIEWER_UI_READY } from 'app/config/editorialReviewer.config';
import { EditorialAccessContext, initialEditorialAccessState as initialState } from 'app/context/editorialAccess.context';
import { useAuthUser } from 'app/hooks/useAuthUser';
import { EDITORIAL_ACCESS_STATUS } from 'app/models/editorialAccess';
import { loadEditorialAccessMe } from 'app/services/editorialAccess.service';
import { pauseEditorialRequestSession, publishEditorialRequestSession, releaseEditorialRequestSession } from 'app/services/editorialRequest.service';
import PropTypes from 'prop-types';
import React from 'react';

function matchesIdentity(state, identity) {
  return state.authToken === identity.authToken && state.userGuid === identity.userGuid && state.userRole === identity.userRole;
}

export default function EditorialAccessProvider({ children, scopeKey = '' }) {
  const { authToken, authUser } = useAuthUser();
  const userGuid = authUser?.guid;
  const userRole = authUser?.role_guid;
  const requestOwner = React.useRef(Symbol('editorial-session'));
  const [state, setState] = React.useState(initialState);
  const [revision, setRevision] = React.useState(0);
  const retry = React.useCallback(() => {
    pauseEditorialRequestSession(requestOwner.current);
    setState(initialState);
    setRevision((value) => value + 1);
  }, []);

  React.useEffect(() => {
    if (!authToken || !userGuid) return undefined;
    const identity = { authToken, userGuid, userRole };
    const controller = new AbortController();
    let cancelled = false;
    let request = 0;

    // Toda recarga revalida en segundo plano: degradar un permiso ya resuelto a "loading"
    // desmontaría la aplicación entera al cambiar de ámbito o al recuperar el foco.
    async function load() {
      pauseEditorialRequestSession(requestOwner.current);
      const currentRequest = ++request;
      setState((previous) => {
        if (previous.status !== EDITORIAL_ACCESS_STATUS.ready || !matchesIdentity(previous, identity)) {
          return { status: EDITORIAL_ACCESS_STATUS.loading, data: null, ...identity };
        }
        return EDITORIAL_REVIEWER_UI_READY ? { ...previous, revalidating: true } : previous;
      });
      try {
        const data = await loadEditorialAccessMe(controller.signal);
        if (!cancelled && currentRequest === request) setState({ status: data ? EDITORIAL_ACCESS_STATUS.ready : EDITORIAL_ACCESS_STATUS.error, data, ...identity });
      } catch {
        if (!cancelled && currentRequest === request) setState({ status: EDITORIAL_ACCESS_STATUS.error, data: null, ...identity });
      }
    }

    load();
    globalThis.addEventListener('focus', load);
    return () => {
      cancelled = true;
      controller.abort();
      globalThis.removeEventListener('focus', load);
    };
  }, [authToken, userGuid, userRole, scopeKey, revision]);

  const updateAccess = React.useCallback(
    (data) => {
      setState((previous) => {
        if (!matchesIdentity(previous, { authToken, userGuid, userRole })) return previous;
        return JSON.stringify(previous.data) === JSON.stringify(data) ? previous : { ...previous, data };
      });
    },
    [authToken, userGuid, userRole]
  );

  const value = React.useMemo(() => {
    const identity = { authToken, userGuid, userRole };
    return authToken && userGuid && matchesIdentity(state, identity)
      ? { status: state.status, data: state.data, revalidating: state.revalidating === true, retry, updateAccess, role: userRole }
      : { ...initialState, retry, updateAccess, role: userRole };
  }, [authToken, userGuid, userRole, state, retry, updateAccess]);

  React.useLayoutEffect(() => {
    publishEditorialRequestSession(requestOwner.current, value, authToken);
  }, [value, authToken]);
  React.useLayoutEffect(() => {
    const owner = requestOwner.current;
    return () => releaseEditorialRequestSession(owner);
  }, []);

  return <EditorialAccessContext.Provider value={value}>{children}</EditorialAccessContext.Provider>;
}

EditorialAccessProvider.propTypes = { children: PropTypes.node, scopeKey: PropTypes.string };
